import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import { Field, FieldGroup, Fieldset, Label } from './common/Catalyst/fieldset';
import { FieldPicker } from './common/Custom/FieldPicker';
import { Input } from './common/Catalyst/input';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import LoadingSpinner from './LoadingSpinner';
import { bankService, ledgerService } from './clients/ApiClients';
import { PayableReturn } from './clients/rust-api-client';
import { apiErrorMessage } from './utils/apiErrors';
import { formatCurrency } from './utils/bankHelpers';

interface VendorPayDialogProps {
  open: boolean;
  onClose: () => void;
  hoaId: string;
  vendorId: string;
  vendorName: string;
  /** The payable as the API returned it — `pending` at the bank, or
   *  `failed` with the bank's reason. */
  onSubmitted: (payable: PayableReturn) => void;
}

interface AcctOption {
  id: string;
  accountNumber: string;
  purpose?: string | null;
}

interface GlOption {
  id: string;
  number: string;
  name: string;
}

interface FundOption {
  id: string;
  code: string;
  name: string;
}

const purposeLabel = (purpose?: string | null): string =>
  purpose ? purpose.charAt(0).toUpperCase() + purpose.slice(1) : 'Account';

const todayIso = (): string => new Date().toISOString().slice(0, 10);

/**
 * Pay a vendor by ACH. **Moves nothing.** Submitting validates the
 * request with the bank (`token`) and hands it over (`add`); the bank
 * then holds it until an Owner or Admin settles the day's batch with
 * their RSA code on the Banking page. The expense account is required
 * because the payment posts to the books on settle, and a payment the
 * ledger cannot take is refused here rather than recorded off-book.
 */
const VendorPayDialog: React.FC<VendorPayDialogProps> = ({
  open,
  onClose,
  hoaId,
  vendorId,
  vendorName,
  onSubmitted,
}) => {
  const [accounts, setAccounts] = useState<AcctOption[]>([]);
  const [expenseAccounts, setExpenseAccounts] = useState<GlOption[]>([]);
  const [funds, setFunds] = useState<FundOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [memo, setMemo] = useState('');
  const [expenseId, setExpenseId] = useState('');
  const [fundId, setFundId] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<PayableReturn | null>(null);

  const resetForm = useCallback(() => {
    setAccountId('');
    setAmount('');
    setEffectiveDate(todayIso());
    setMemo('');
    setExpenseId('');
    setError(null);
    setResult(null);
  }, []);

  // The pickers are store-only reads, so the dialog opens locally even
  // where the bank call behind Submit cannot complete.
  useEffect(() => {
    if (!open) return;
    resetForm();
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [acctResp, glResp, fundResp] = await Promise.all([
          bankService.listHoaAccounts({ hoaId }),
          ledgerService.listAccounts({ hoaId }),
          ledgerService.listFunds({ hoaId }),
        ]);
        if (cancelled) return;
        const active = acctResp.data.accounts.filter((a) => a.is_active);
        setAccounts(
          active.map((a) => ({
            id: a.id,
            accountNumber: a.account_number,
            purpose: a.purpose,
          }))
        );
        if (active.length === 1) setAccountId(active[0].id);
        setExpenseAccounts(
          glResp.data.accounts
            .filter((a) => a.account_type === 'expense' && a.is_active)
            .sort((a, b) => a.account_number.localeCompare(b.account_number))
            .map((a) => ({ id: a.id, number: a.account_number, name: a.name }))
        );
        const activeFunds = fundResp.data.funds.filter((f) => f.is_active);
        setFunds(
          activeFunds.map((f) => ({ id: f.id, code: f.code, name: f.name }))
        );
        const operating = activeFunds.find((f) => f.code === 'operating');
        setFundId(operating?.id ?? activeFunds[0]?.id ?? '');
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          apiErrorMessage(err, "Couldn't load this HOA’s accounts.")
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, hoaId, resetForm]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    if (!accountId) {
      setError('Choose the account to pay from.');
      return;
    }
    const amt = Number(amount);
    if (!amount || isNaN(amt) || amt <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(amount.trim())) {
      setError('Amount is dollars and cents, like 125.00.');
      return;
    }
    if (!effectiveDate) {
      setError('Choose the effective date.');
      return;
    }
    if (!expenseId) {
      setError('Choose the expense account the payment is for.');
      return;
    }
    try {
      setSubmitting(true);
      const resp = await bankService.submitVendorPayment({
        hoaId,
        vendorPaymentCreate: {
          vendor_id: vendorId,
          hoa_bank_account_id: accountId,
          amount: amount.trim(),
          effective_date: effectiveDate,
          memo: memo.trim() || null,
          expense_gl_account_id: expenseId,
          fund_id: fundId || null,
        },
      });
      setResult(resp.data);
      onSubmitted(resp.data);
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't submit the payment."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Pay {vendorName} by ACH</DialogTitle>
      <DialogDescription>
        The bank validates the payment and holds it. Nothing moves until an
        Owner or Admin settles it on the Banking page with their RSA code.
      </DialogDescription>
      <DialogBody>
        {error && (
          <ErrorAlert
            className="mb-4"
            title="Not submitted"
            onDismiss={() => setError(null)}
          >
            {error}
          </ErrorAlert>
        )}

        {result ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Badge color={result.status === 'pending' ? 'amber' : 'red'}>
                {result.status === 'pending' ? 'Awaiting settle' : 'Failed'}
              </Badge>
              <Text className="text-sm">
                {formatCurrency(Number(result.amount))}
                {result.bank_transaction_id
                  ? ` · bank #${result.bank_transaction_id}`
                  : ''}
              </Text>
            </div>
            {result.status === 'pending' ? (
              <Text className="text-sm">
                The bank has it. It appears under{' '}
                <strong>ACH payments to settle</strong> on the Banking page; the
                money moves on the bank’s next processing day after it is
                settled.
              </Text>
            ) : (
              <Text className="text-sm">
                The bank did not take it:{' '}
                {result.error_message ?? 'no reason given'}
                {result.error_code ? ` (code ${result.error_code})` : ''}. Fix
                the cause and submit again; this one cannot be retried.
              </Text>
            )}
          </div>
        ) : loading ? (
          <LoadingSpinner />
        ) : loadError ? (
          <ErrorAlert>{loadError}</ErrorAlert>
        ) : accounts.length === 0 ? (
          <Text>Assign a bank account to this HOA before paying a vendor.</Text>
        ) : (
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Pay from</Label>
                <FieldPicker
                  label="Pay from"
                  value={accountId}
                  onChange={setAccountId}
                  placeholder="Select an account"
                  options={accounts.map((a) => ({
                    value: a.id,
                    label: `${purposeLabel(a.purpose)} — ${a.accountNumber}`,
                  }))}
                />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>Amount (USD)</Label>
                  <Input
                    name="amount"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Effective date</Label>
                  <Input
                    name="effective_date"
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                  />
                </Field>
              </div>
              <Field>
                <Label>Description</Label>
                <Input
                  name="memo"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  maxLength={80}
                  placeholder={`Optional — defaults to “Payment to ${vendorName}”`}
                />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>Expense account</Label>
                  <FieldPicker
                    label="Expense account"
                    value={expenseId}
                    onChange={setExpenseId}
                    placeholder="Required"
                    options={expenseAccounts.map((a) => ({
                      value: a.id,
                      label: `${a.number} — ${a.name}`,
                    }))}
                  />
                </Field>
                <Field>
                  <Label>Fund</Label>
                  <FieldPicker
                    label="Fund"
                    value={fundId}
                    onChange={setFundId}
                    options={funds.map((f) => ({ value: f.id, label: f.name }))}
                  />
                </Field>
              </div>
              <Text className="text-xs">
                On settle, the expense is debited and the account’s cash is
                credited in this fund. The bank prints the description on the
                vendor’s statement.
              </Text>
            </FieldGroup>
          </Fieldset>
        )}
      </DialogBody>
      <DialogActions>
        {result ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button plain onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              color="sky"
              onClick={handleSubmit}
              disabled={
                submitting || loading || !!loadError || accounts.length === 0
              }
            >
              {submitting ? 'Submitting…' : 'Submit to the bank'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VendorPayDialog;
